/**
 * Karakter- és riválislista. Az adatok a balance-dokumentumhoz igazodnak:
 * a 4 választható karakter 35 pontos, a riválisok 31–35 pontos stat-összeggel.
 */
import type { AiPersona, CharacterDef } from '../core/types';

/** A 4 választható karakter, ebben a sorrendben. */
export const CHARACTERS: CharacterDef[] = [
  {
    id: 'soma',
    name: 'Soma',
    short: 'Soma',
    age: 12,
    role: 'Gokartos kölyök',
    bio: 'A gokartot a nagypapától kapta, a féket viszont még nem sikerült beszerelni. Szerinte a kanyar csak egyfajta sebesség.',
    colors: { body: '#ff5c4d', accent: '#ffc857', skin: '#f2c29b', hair: '#6b4a2f' },
    vehicle: { kind: 'kart', name: 'Tücsök Gokart', length: 40, width: 26 },
    stats: { topSpeed: 6, accel: 9, handling: 8.5, grip: 7.5, weight: 4 },
    ability: {
      id: 'hop',
      name: 'Ugrás',
      desc: 'Egy rövid ugrás: a járdán is megtartja a sebességét.',
      cooldown: 7,
      duration: 0.9,
    },
    selectable: true,
  },
  {
    id: 'boroka',
    name: 'Boróka',
    short: 'Boróka',
    age: 16,
    role: 'E-roller fenomén',
    bio: 'Az e-rollert három hónap alatt tuningolta fel odáig, hogy már a roller is fél tőle. Minden reggel versenyt nyer a suliba menet – a busz ellen.',
    colors: { body: '#3ec6c0', accent: '#ff5c4d', skin: '#f0c9a8', hair: '#2b2b33' },
    vehicle: { kind: 'scooter', name: 'Villámroller 3000', length: 36, width: 20 },
    stats: { topSpeed: 6.5, accel: 8.5, handling: 9.5, grip: 8, weight: 2.5 },
    ability: {
      id: 'dash',
      name: 'Rakétarúgás',
      desc: 'Egy löketnyi gyorsítás, amivel kanyarból is kilő.',
      cooldown: 9,
      duration: 1.1,
    },
    selectable: true,
  },
  {
    id: 'anya',
    name: 'Anya',
    short: 'Anya',
    age: 45,
    role: 'Családi logisztika főnöke',
    bio: 'A bevásárló-pajzsot a heti nagybevásárlás ihlette, ami nélkül el sem indul. A kanyarokban is fejben a bevásárlólistát tervezi.',
    colors: { body: '#4f9d69', accent: '#ffc857', skin: '#e8b28c', hair: '#5a3a2a' },
    vehicle: { kind: 'minivan', name: 'Zöld Bálna', length: 52, width: 30 },
    stats: { topSpeed: 8.5, accel: 5.5, handling: 5.5, grip: 7.5, weight: 8 },
    ability: {
      id: 'shield',
      name: 'Bevásárló-pajzs',
      desc: '4 mp-ig véd a találatoktól a bevásárló-pajzs.',
      cooldown: 12,
      duration: 4,
    },
    selectable: true,
  },
  {
    id: 'apa',
    name: 'Apa',
    short: 'Apa',
    age: 52,
    role: 'A hétvégi szerelő',
    bio: 'Az Öreg Harcost még 2003-ban ragasztotta össze, és azóta sem esett szét – szerinte ez felér egy garanciával. A nitró gombot mindig a szervizkönyv helyén tartja.',
    colors: { body: '#5b7cba', accent: '#f4f1e8', skin: '#e0ab84', hair: '#3a2f2a' },
    vehicle: { kind: 'wagon', name: 'Öreg Harcos', length: 50, width: 28 },
    stats: { topSpeed: 7.5, accel: 7, handling: 7, grip: 7.5, weight: 6 },
    ability: {
      id: 'nitro',
      name: 'Benzinboost',
      desc: '1.8 mp-ig tartó nitrólöket, egyenesben a legerősebb.',
      cooldown: 11,
      duration: 1.8,
    },
    selectable: true,
  },
];

/** AI-only riválisok. */
export const RIVALS: CharacterDef[] = [
  {
    id: 'robi',
    name: 'Robi, az ételfutár',
    short: 'Robi',
    age: 24,
    role: 'Ételfutár',
    bio: 'A forró csomagot sosem adja át hidegen, mert akkor csúszik a borravaló. A közlekedési szabályokat inkább irányelveknek tekinti.',
    colors: { body: '#ff8a3d', accent: '#2b2b33', skin: '#e8b28c', hair: '#2b2b33' },
    vehicle: { kind: 'courier', name: 'Robogó Robi', length: 38, width: 22 },
    stats: { topSpeed: 7, accel: 8, handling: 9, grip: 8, weight: 3 },
    ability: {
      id: 'dash',
      name: 'Forró csomag',
      desc: 'A csomagot maga elé tolva 1 mp-ig gyorsít.',
      cooldown: 9,
      duration: 1,
    },
    selectable: false,
  },
  {
    id: 'gabor',
    name: 'Gábor, a csomagküldő',
    short: 'Gábor',
    age: 38,
    role: 'Csomagküldő',
    bio: 'A raktérben mindig van egy sérthetetlen csomag, ami elől pajzsként is megteszi. A kanyarokat irányítószám szerint veszi be.',
    colors: { body: '#b08b5a', accent: '#efe7d2', skin: '#e0ab84', hair: '#4a3a2a' },
    vehicle: { kind: 'van', name: 'Csomagpresszó', length: 56, width: 32 },
    stats: { topSpeed: 8, accel: 5, handling: 5.5, grip: 7, weight: 9.5 },
    ability: {
      id: 'shield',
      name: 'Csomagpajzs',
      desc: '3 mp-ig véd a csomagolópapír-pajzs.',
      cooldown: 14,
      duration: 3,
    },
    selectable: false,
  },
  {
    id: 'tibi',
    name: 'Tibi, a kopasz BMW-s',
    short: 'Tibi',
    age: 34,
    role: 'Kopasz vagány',
    bio: 'A Fekete BMW-n a villogás alapfelszereltség, a féklámpa viszont extra. Azt mondja, ő nem előzni szokott, hanem megérkezni.',
    colors: { body: '#23252b', accent: '#c0c6d4', skin: '#e0ab84', hair: '#23252b' },
    vehicle: { kind: 'sedan', name: 'Fekete BMW', length: 50, width: 27 },
    stats: { topSpeed: 8.5, accel: 7, handling: 6.5, grip: 6.5, weight: 6.5 },
    ability: {
      id: 'nitro',
      name: 'Villogás',
      desc: 'Fényszóró-villogás, 1.2 mp nitró.',
      cooldown: 10,
      duration: 1.2,
    },
    selectable: false,
  },
  {
    id: 'bela',
    name: 'Béla bácsi, a hétvégi autós',
    short: 'Béla',
    age: 71,
    role: 'Nyugdíjas sofőr',
    bio: 'A Bogárral vasárnap csak a templomig szokott járni, de a verseny az más. A pajzsot a hosszú házasság tanította: néha egyszerűen nem ér semmi.',
    colors: { body: '#8fb3a1', accent: '#d9c79a', skin: '#e8c0a0', hair: '#cfcfcf' },
    vehicle: { kind: 'beetle', name: 'Bogár', length: 44, width: 26 },
    stats: { topSpeed: 6.5, accel: 6, handling: 5.5, grip: 7, weight: 6 },
    ability: {
      id: 'shield',
      name: 'Vasárnapi nyugalom',
      desc: '2.5 mp-ig semmi sem zavarja.',
      cooldown: 13,
      duration: 2.5,
    },
    selectable: false,
  },
  {
    id: 'krisztina',
    name: 'Krisztina, a sietős üzletasszony',
    short: 'Krisztina',
    age: 33,
    role: 'Sietős üzletasszony',
    bio: 'A Városi Cápa a megbeszélések között néha beugrik egy gyors körre. A nitróját is naptárblokkban használja, negyedórára.',
    colors: { body: '#b0457f', accent: '#f4f1e8', skin: '#f0c9a8', hair: '#3a2f2a' },
    vehicle: { kind: 'hatch', name: 'Városi Cápa', length: 46, width: 26 },
    stats: { topSpeed: 8, accel: 7.5, handling: 6, grip: 6, weight: 5 },
    ability: {
      id: 'nitro',
      name: 'Megbeszélés',
      desc: '1.4 mp tiszta gyorsítás, mint egy rövid megbeszélés.',
      cooldown: 9,
      duration: 1.4,
    },
    selectable: false,
  },
];

/** AI-személyiségek: agresszió, hibaarány, türelem, felzárkózás. */
const PERSONAS: Record<string, AiPersona> = {
  soma: { aggression: 0.6, mistake: 0.18, patience: 0.3, catchup: 0.65 },
  boroka: { aggression: 0.78, mistake: 0.2, patience: 0.2, catchup: 0.75 },
  anya: { aggression: 0.45, mistake: 0.1, patience: 0.75, catchup: 0.5 },
  apa: { aggression: 0.5, mistake: 0.12, patience: 0.7, catchup: 0.45 },
  robi: { aggression: 0.92, mistake: 0.25, patience: 0.1, catchup: 0.9 },
  gabor: { aggression: 0.4, mistake: 0.12, patience: 0.65, catchup: 0.5 },
  tibi: { aggression: 0.95, mistake: 0.3, patience: 0.05, catchup: 0.92 },
  bela: { aggression: 0.22, mistake: 0.06, patience: 0.9, catchup: 0.2 },
  krisztina: { aggression: 0.72, mistake: 0.14, patience: 0.4, catchup: 0.72 },
};

for (const character of [...CHARACTERS, ...RIVALS]) {
  const persona = PERSONAS[character.id];
  if (persona) character.persona = persona;
}

/** Minden karakter: a választhatók elöl, utánuk a riválisok. */
export const ALL_CHARACTERS: CharacterDef[] = [...CHARACTERS, ...RIVALS];

const BY_ID = new Map<string, CharacterDef>(ALL_CHARACTERS.map((c) => [c.id, c] as const));

/** Karakter id alapján; ismeretlen id-re hibát dob. */
export function characterById(id: string): CharacterDef {
  const found = BY_ID.get(id);
  if (!found) throw new Error(`Ismeretlen karakter: ${id}`);
  return found;
}
